// Fulfilment: how a requested line reaches the job. The field makes one offer, the office picks
// a route per line, the field hears the plan once. Shared by Materials (the ask and the answer)
// and Procurement (the decision) through the SDK, so both say the same words.
//
// Company settings (settings/fulfilment) decide which routes the office is offered:
//   stock   we keep stock at a shop            → the shop routes exist at all
//   deliver never | adhoc | scheduled           → "Company delivers" exists, and whether runs sit on days
//   shelf   we have a pickup shelf              → "Crew picks up at the shop" exists
import { useQuery } from '@tanstack/react-query';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../auth';

export type Route = 'vendor' | 'willcall' | 'shop' | 'run';
export interface Plan { route: Route; date: string | null; note: string | null; poNum?: string | null; by?: string | null; at?: string | null }
export interface Offer { kind: 'pickup' | 'deliver'; day: string | null }
export interface FulfilmentSettings { stock: boolean; deliver: 'never' | 'adhoc' | 'scheduled'; runDays: number[]; shelf: boolean }
export const DEFAULT_FULFILMENT: FulfilmentSettings = { stock: true, deliver: 'adhoc', runDays: [], shelf: true };

/** Route id, the office's label, the field's words. */
export const ROUTES: Array<{ id: Route; label: string; field: string; hint: string }> = [
  { id: 'vendor', label: 'Vendor delivers', field: 'Vendor delivers to the job', hint: 'The default. Goes on a purchase order; the vendor brings it to the site.' },
  { id: 'willcall', label: 'Pick up at the supply house', field: 'Holding at the supply house', hint: 'On the purchase order marked will-call; a crew member grabs it at the counter.' },
  { id: 'shop', label: 'Crew picks up at the shop', field: 'On the pickup shelf at the shop', hint: 'Staged on the pickup shelf; the crew takes it on the way in.' },
  { id: 'run', label: 'Company delivers', field: 'Coming on the truck', hint: 'A run from the shop: a driver, a day, returns ride back.' },
];
export const routeLabel = (r: Route) => ROUTES.find(x => x.id === r)?.label ?? r;
/** The routes a company's settings allow. Vendor and will-call always; the shop routes need stock. */
export function routesFor(st: FulfilmentSettings): Route[] {
  const out: Route[] = ['vendor', 'willcall'];
  if (st.stock && st.shelf) out.push('shop');
  if (st.stock && st.deliver !== 'never') out.push('run');
  return out;
}
const day = (iso: string | null | undefined) => iso ? new Date(iso + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }) : '';
/** One line of plain words for the field: "Vendor delivers Thu Oct 15 · Graybar" */
export function planText(p: Plan | null | undefined): string {
  if (!p) return '';
  const when = p.date ? ` ${day(p.date)}` : '';
  switch (p.route) {
    case 'vendor': return `Vendor delivers${when}${p.poNum ? ` · ${p.poNum}` : ''}${p.note ? ` · ${p.note}` : ''}`;
    case 'willcall': return `Holding at ${p.note || 'the supply house'}${p.date ? `, grab it ${day(p.date)}` : ''}${p.poNum ? ` · ${p.poNum}` : ''}`;
    case 'shop': return `On the pickup shelf at the shop${p.date ? `, grab it ${day(p.date)}` : ''}${p.note ? ` · ${p.note}` : ''}`;
    case 'run': return `${p.note || 'The truck'} brings it${when}`;
    default: return '';
  }
}
export const offerText = (o: Offer | null | undefined) => !o ? '' : o.kind === 'pickup' ? `Can pick up at the shop${o.day ? ` ${day(o.day)}` : ''}` : 'Needs to come to the job';

export function useFulfilment() {
  const { session } = useAuth();
  const cid = session?.paths.companyId ?? 'none';
  return useQuery({ queryKey: ['fulfilment', cid], enabled: !!session, staleTime: 10 * 60_000, queryFn: async (): Promise<FulfilmentSettings> => {
    try { const d = await getDoc(doc(db, `${session!.paths.settings}/fulfilment`)); if (!d.exists()) return DEFAULT_FULFILMENT; const x = d.data(); return { stock: x.stock !== false, deliver: x.deliver === 'never' || x.deliver === 'scheduled' ? x.deliver : 'adhoc', runDays: Array.isArray(x.runDays) ? x.runDays.map(Number).filter(n => n >= 0 && n <= 6) : [], shelf: x.shelf !== false }; }
    catch { return DEFAULT_FULFILMENT; }
  } });
}
export async function saveFulfilment(settingsPath: string, st: FulfilmentSettings, byUid: string) {
  await setDoc(doc(db, `${settingsPath}/fulfilment`), { ...st, updatedAt: serverTimestamp(), updatedBy: byUid }, { merge: true });
}
